package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.models.Patient
import com.example.borshi.web.nutritionist.models.PatientStatus
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Add User Modal for creating new patient/user records
 * Connects to Firebase 'users' collection
 */
@Composable
fun AddUserModal(
    isOpen: Boolean,
    repository: NutritionistRepository,
    nutritionistId: String,
    onClose: () -> Unit,
    onUserAdded: () -> Unit
) {
    if (!isOpen) return

    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var age by remember { mutableStateOf("") }
    var gender by remember { mutableStateOf("") }
    var height by remember { mutableStateOf("") }
    var weight by remember { mutableStateOf("") }
    var healthGoals by remember { mutableStateOf("") }
    var healthConditions by remember { mutableStateOf("") }
    var allergies by remember { mutableStateOf("") }
    var dietaryRestrictions by remember { mutableStateOf("") }

    var isSubmitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Modal Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        // Modal Content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(600.px)
                width(100.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    padding(20.px)
                    property("border-bottom", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                H2({ style { margin(0.px); fontSize(24.px); color(Color("#333")) } }) {
                    Text("➕ Add New User")
                }
                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        border(0.px)
                        fontSize(24.px)
                        cursor("pointer")
                        padding(0.px)
                        width(32.px)
                        height(32.px)
                    }
                    onClick { onClose() }
                }) {
                    Text("×")
                }
            }

            // Form
            Div({ style { padding(20.px) } }) {
                if (error != null) {
                    ErrorMessage(error!!)
                }

                // Basic Information
                FormSection("Basic Information") {
                    FormField("Name *", "name") {
                        Input(InputType.Text) {
                            value(name)
                            onInput { name = it.value }
                            attr("required", "true")
                            attr("placeholder", "Full name")
                        }
                    }

                    FormField("Email *", "email") {
                        Input(InputType.Email) {
                            value(email)
                            onInput { email = it.value }
                            attr("required", "true")
                            attr("placeholder", "email@example.com")
                        }
                    }

                    FormField("Phone", "phone") {
                        Input(InputType.Tel) {
                            value(phone)
                            onInput { phone = it.value }
                            attr("placeholder", "+880 1XXX-XXXXXX")
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "1fr 1fr")
                            gap(12.px)
                        }
                    }) {
                        FormField("Age", "age") {
                            Input(InputType.Number) {
                                value(age)
                                onInput { age = it.value?.toString() ?: "" }
                                attr("placeholder", "25")
                                attr("min", "1")
                            }
                        }

                        FormField("Gender", "gender") {
                            Select({
                                style {
                                    width(100.percent)
                                    padding(10.px)
                                    border(1.px, LineStyle.Solid, Color("#ddd"))
                                    borderRadius(6.px)
                                    fontSize(14.px)
                                }
                                onChange { gender = it.value ?: "" }
                            }) {
                                Option("") { Text("Select gender") }
                                Option("Male") { Text("Male") }
                                Option("Female") { Text("Female") }
                                Option("Other") { Text("Other") }
                            }
                        }
                    }
                }

                // Physical Metrics
                FormSection("Physical Metrics") {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "1fr 1fr")
                            gap(12.px)
                        }
                    }) {
                        FormField("Height (cm)", "height") {
                            Input(InputType.Number) {
                                value(height)
                                onInput { height = it.value?.toString() ?: "" }
                                attr("placeholder", "170")
                                attr("min", "50")
                                attr("step", "0.1")
                            }
                        }

                        FormField("Weight (kg)", "weight") {
                            Input(InputType.Number) {
                                value(weight)
                                onInput { weight = it.value?.toString() ?: "" }
                                attr("placeholder", "70")
                                attr("min", "20")
                                attr("step", "0.1")
                            }
                        }
                    }
                }

                // Health Information
                FormSection("Health Information") {
                    FormField("Health Goals", "goals") {
                        Input(InputType.Text) {
                            value(healthGoals)
                            onInput { healthGoals = it.value }
                            attr("placeholder", "e.g., Weight Loss, Muscle Building")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Health Conditions", "conditions") {
                        Input(InputType.Text) {
                            value(healthConditions)
                            onInput { healthConditions = it.value }
                            attr("placeholder", "e.g., Diabetes, Hypertension")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Allergies", "allergies") {
                        Input(InputType.Text) {
                            value(allergies)
                            onInput { allergies = it.value }
                            attr("placeholder", "e.g., Shellfish, Nuts")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Dietary Restrictions", "restrictions") {
                        Input(InputType.Text) {
                            value(dietaryRestrictions)
                            onInput { dietaryRestrictions = it.value }
                            attr("placeholder", "e.g., Vegetarian, Gluten-free")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }
                }
            }

            // Footer
            Div({
                style {
                    padding(20.px)
                    property("border-top", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(Color("transparent"))
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        cursor("pointer")
                        fontSize(14.px)
                    }
                    if (isSubmitting) {
                        attr("disabled", "disabled")
                    }
                    onClick { onClose() }
                }) {
                    Text("Cancel")
                }

                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(if (isSubmitting) Color("#ccc") else Color("#4CAF50"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(6.px)
                        cursor(if (isSubmitting) "not-allowed" else "pointer")
                        fontSize(14.px)
                        fontWeight("600")
                    }
                    if (isSubmitting) {
                        attr("disabled", "disabled")
                    }
                    onClick {
                        scope.launch {
                            if (name.isBlank() || email.isBlank()) {
                                error = "Name and Email are required"
                                return@launch
                            }

                            isSubmitting = true
                            error = null

                            try {
                                val heightValue = height.toDoubleOrNull() ?: 0.0
                                val weightValue = weight.toDoubleOrNull() ?: 0.0
                                val bmi = if (heightValue > 0 && weightValue > 0) {
                                    weightValue / ((heightValue / 100) * (heightValue / 100))
                                } else 0.0

                                val patient = Patient(
                                    patientId = "", // Will be assigned by Firebase
                                    userId = "",
                                    name = name,
                                    email = email,
                                    phone = phone,
                                    age = age.toIntOrNull() ?: 0,
                                    gender = gender,
                                    height = heightValue,
                                    weight = weightValue,
                                    bmi = bmi,
                                    healthGoals = healthGoals.split(",").map { it.trim() }.filter { it.isNotEmpty() },
                                    healthConditions = healthConditions.split(",").map { it.trim() }.filter { it.isNotEmpty() },
                                    allergies = allergies.split(",").map { it.trim() }.filter { it.isNotEmpty() },
                                    dietaryRestrictions = dietaryRestrictions.split(",").map { it.trim() }.filter { it.isNotEmpty() },
                                    assignedNutritionistId = nutritionistId,
                                    status = PatientStatus.ACTIVE,
                                    createdAt = kotlin.js.Date.now().toLong(),
                                    updatedAt = kotlin.js.Date.now().toLong()
                                )

                                repository.createUser(patient)
                                    .onSuccess { userId ->
                                        console.log("✅ User created with ID: $userId")
                                        onUserAdded()
                                        onClose()
                                    }
                                    .onFailure {
                                        error = "Failed to add user: ${it.message}"
                                        isSubmitting = false
                                    }
                            } catch (e: Exception) {
                                error = "Error: ${e.message}"
                                isSubmitting = false
                            }
                        }
                    }
                }) {
                    Text(if (isSubmitting) "Adding..." else "Add User")
                }
            }
        }
    }
}

@Composable
fun FormSection(title: String, content: @Composable () -> Unit) {
    Div({
        style {
            marginBottom(24.px)
        }
    }) {
        H3({
            style {
                fontSize(16.px)
                marginBottom(12.px)
                color(Color("#333"))
                fontWeight("600")
            }
        }) {
            Text(title)
        }
        content()
    }
}

@Composable
fun FormField(label: String, id: String, content: @Composable () -> Unit) {
    Div({
        style {
            marginBottom(16.px)
        }
    }) {
        Label(forId = id) {
            Span({
                style {
                    display(DisplayStyle.Block)
                    marginBottom(6.px)
                    fontSize(14.px)
                    fontWeight("500")
                    color(Color("#555"))
                }
            }) {
                Text(label)
            }
        }
        content()
    }
}

